import { useState } from "react";
import { toast } from "sonner";
import { X, Plus, Trash } from "@phosphor-icons/react";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { inputCls, btnPrimary, btnGhost } from "./shared";
import { DistributionPicker, useHierarchy } from "./DistributionPicker";

const EMPTY_ITEM = { title: "", description: "", section: "", proof_type: "boolean", unit: "", min_value: "", max_value: "", photo_required: false, comment_required: false, critical: false };

function Chip({ on, onClick, children, testId, tone = "bg-slate-900 border-slate-900" }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick}
      className={`h-9 px-3.5 rounded-lg text-xs font-bold border tap-press ${on ? `${tone} text-white` : "bg-white text-slate-600 border-slate-300 hover:border-slate-900"}`}>
      {children}
    </button>
  );
}

export function TemplateEditor({ initial, config, departments, venues, onSaved, onClose, headerNode }) {
  const { user } = useAuth();
  const isTop = ["admin", "gm"].includes(user.role) || user.is_master_operator;
  const isArea = ["cluster", "regional"].includes(user.role);
  const allowGlobal = isTop || !!user.full_access;
  const hier = useHierarchy();
  const myDepts = user.department_ids || [];
  const scoped = !isTop && !isArea && myDepts.length > 0;
  const [form, setForm] = useState(() => ({
    name: initial?.name || "", description: initial?.description || "", category: initial?.category || "custom",
    department_ids: initial?.department_ids?.length ? initial.department_ids : (scoped ? myDepts : []),
    venue_ids: initial?.venue_ids || [], node_ids: initial?.node_ids || [], shifts: initial?.shifts?.length ? initial.shifts : ["morning"],
    active: initial?.active ?? true,
    items: (initial?.items?.length ? initial.items : [EMPTY_ITEM]).map((it) => ({ ...EMPTY_ITEM, ...it, min_value: it.min_value ?? "", max_value: it.max_value ?? "" })),
  }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, v) => set(k, form[k].includes(v) ? form[k].filter((x) => x !== v) : [...form[k], v]);
  const setItem = (i, patch) => set("items", form.items.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const deptOptions = departments.filter((d) => d.active !== false && (!scoped || myDepts.includes(d.id)));
  const categories = config?.categories || [];

  const save = async () => {
    if (!form.name.trim()) return toast.error("Template name is required");
    const items = form.items.filter((it) => it.title.trim()).map((it) => ({
      ...it, min_value: it.min_value === "" ? null : Number(it.min_value), max_value: it.max_value === "" ? null : Number(it.max_value),
    }));
    if (!items.length) return toast.error("Add at least one checklist line");
    if ((isTop || isArea) && !allowGlobal && form.node_ids.length + form.venue_ids.length === 0) return toast.error("Pick at least one group or venue to distribute this template to");
    setBusy(true);
    try {
      const body = { ...form, items, source: initial?.source || { type: "manual" } };
      const { data } = initial?.id ? await api.put(`/checklists/templates/${initial.id}`, body) : await api.post("/checklists/templates", body);
      toast.success(initial?.id ? "Template updated" : "Template activated");
      onSaved(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-slate-950/50 flex items-end sm:items-center justify-center sm:p-6" onClick={onClose}>
      <div data-testid="template-editor" onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-5xl max-h-[94vh] sm:max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 sm:px-8 py-4 flex items-center justify-between z-10">
          <h2 className="font-heading text-xl sm:text-2xl font-bold">{initial?.id ? "Edit template" : "New checklist template"}</h2>
          <button data-testid="template-editor-close" onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X size={22} /></button>
        </div>
        <div className="px-5 sm:px-8 py-6 space-y-6">
          {headerNode}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-400">Template name</label>
              <input data-testid="template-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Fridge / Freezer Temperature Log" className={`${inputCls} mt-1`} />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-400">Category</label>
              <select data-testid="template-category-select" value={form.category} onChange={(e) => set("category", e.target.value)} className={`${inputCls} mt-1`}>
                {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                {!categories.some((c) => c.key === form.category) && <option value={form.category}>{form.category}</option>}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-400">Description</label>
              <input data-testid="template-description-input" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Optional" className={`${inputCls} mt-1`} />
            </div>
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-400 mb-2">Shifts</p>
            <div className="flex flex-wrap gap-2">
              {(config?.shifts || []).map((s) => <Chip key={s.key} testId={`template-shift-${s.key}`} on={form.shifts.includes(s.key)} onClick={() => toggle("shifts", s.key)}>{s.label} <span className="opacity-60 font-semibold">{s.start}–{s.end}</span></Chip>)}
            </div>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-400 mb-2">Departments {scoped ? "(your scope)" : "— none selected = every department"}</p>
            <div className="flex flex-wrap gap-2">
              {deptOptions.map((d) => <Chip key={d.id} testId={`template-dept-${d.id}`} tone="bg-sky-600 border-sky-600" on={form.department_ids.includes(d.id)} onClick={() => { if (scoped && form.department_ids.length === 1 && form.department_ids.includes(d.id)) return; toggle("department_ids", d.id); }}>{d.name}</Chip>)}
            </div>
          </div>
          {(isTop || isArea) && (
            <DistributionPicker hier={hier} venues={venues} allowGlobal={allowGlobal} nodeIds={form.node_ids} venueIds={form.venue_ids}
              onChange={({ node_ids, venue_ids }) => setForm((f) => ({ ...f, node_ids, venue_ids }))} />
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-400">Checklist lines ({form.items.length})</p>
              <label className="flex items-center gap-2 text-xs font-bold text-slate-600"><input data-testid="template-active-toggle" type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="h-4 w-4" /> Active</label>
            </div>
            <div className="overflow-x-auto border border-slate-200 rounded-xl">
              <table className="w-full text-sm min-w-[1040px]">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left w-8">#</th>
                    <th className="px-3 py-2 text-left">Line / checkpoint</th>
                    <th className="px-3 py-2 text-left w-36">Section</th>
                    <th className="px-3 py-2 text-left w-32">Proof</th>
                    <th className="px-3 py-2 text-left w-24">Min</th>
                    <th className="px-3 py-2 text-left w-24">Max</th>
                    <th className="px-3 py-2 text-left w-20">Unit</th>
                    <th className="px-3 py-2 text-center w-14">Photo</th>
                    <th className="px-3 py-2 text-center w-14">Note</th>
                    <th className="px-3 py-2 text-center w-14">Crit.</th>
                    <th className="px-3 py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {form.items.map((it, i) => {
                    const num = it.proof_type === "numeric";
                    const cell = "h-9 px-2 rounded-md border border-slate-200 text-sm w-full bg-white focus:outline-none focus:border-slate-900";
                    return (
                      <tr key={i} data-testid={`template-line-${i}`}>
                        <td className="px-3 py-1.5 text-slate-400 font-bold">{i + 1}</td>
                        <td className="px-3 py-1.5"><input data-testid={`item-title-${i}`} value={it.title} onChange={(e) => setItem(i, { title: e.target.value })} placeholder="Checkpoint" className={cell} /></td>
                        <td className="px-3 py-1.5"><input data-testid={`item-section-${i}`} value={it.section} onChange={(e) => setItem(i, { section: e.target.value })} placeholder="—" className={cell} /></td>
                        <td className="px-3 py-1.5">
                          <select data-testid={`item-type-${i}`} value={it.proof_type} onChange={(e) => setItem(i, { proof_type: e.target.value })} className={cell}>
                            <option value="boolean">Pass / Fail</option>
                            <option value="numeric">Numeric</option>
                            <option value="photo">Photo</option>
                            <option value="text">Text / Reason</option>
                          </select>
                        </td>
                        <td className="px-3 py-1.5"><input data-testid={`item-min-${i}`} type="number" step="any" disabled={!num} value={it.min_value} onChange={(e) => setItem(i, { min_value: e.target.value })} className={`${cell} disabled:bg-slate-50`} /></td>
                        <td className="px-3 py-1.5"><input data-testid={`item-max-${i}`} type="number" step="any" disabled={!num} value={it.max_value} onChange={(e) => setItem(i, { max_value: e.target.value })} className={`${cell} disabled:bg-slate-50`} /></td>
                        <td className="px-3 py-1.5"><input data-testid={`item-unit-${i}`} disabled={!num} value={it.unit} onChange={(e) => setItem(i, { unit: e.target.value })} placeholder="°C" className={`${cell} disabled:bg-slate-50`} /></td>
                        <td className="px-3 py-1.5 text-center"><input data-testid={`item-photo-req-${i}`} type="checkbox" disabled={it.proof_type === "photo"} checked={it.proof_type === "photo" || it.photo_required} onChange={(e) => setItem(i, { photo_required: e.target.checked })} className="h-4 w-4" /></td>
                        <td className="px-3 py-1.5 text-center"><input data-testid={`item-comment-req-${i}`} type="checkbox" disabled={it.proof_type === "text"} checked={it.proof_type === "text" || it.comment_required} onChange={(e) => setItem(i, { comment_required: e.target.checked })} className="h-4 w-4" /></td>
                        <td className="px-3 py-1.5 text-center"><input data-testid={`item-critical-${i}`} type="checkbox" checked={it.critical} onChange={(e) => setItem(i, { critical: e.target.checked })} className="h-4 w-4" /></td>
                        <td className="px-2 py-1.5"><button data-testid={`item-remove-${i}`} onClick={() => set("items", form.items.filter((_, j) => j !== i))} className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50"><Trash size={16} /></button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <button data-testid="template-add-line" onClick={() => set("items", [...form.items, { ...EMPTY_ITEM }])} className={`${btnGhost} mt-3 flex items-center gap-1.5`}><Plus size={16} /> Add line</button>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200">
            <button onClick={onClose} className={btnGhost}>Cancel</button>
            <button data-testid="template-save-button" onClick={save} disabled={busy} className={btnPrimary}>{busy ? "Saving…" : initial?.id ? "Save changes" : "Activate template"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
